import React, { useEffect, useRef } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { IconButton, InputAdornment, TextField } from '@mui/material';
import { SearchInputProps } from '../types/common.types';

const DEBOUNCE_MS = 400;

const SearchInput: React.FC<SearchInputProps> = ({
  label = 'Search',
  value,
  onChange,
  onSearch,
  placeholder = 'Search',
  disabled = false,
  fullWidth = true,
}) => {
  const isFirstRender = useRef(true);
  const onSearchRef = useRef(onSearch);
  onSearchRef.current = onSearch;

  // Auto-search as the user types, once they pause — Enter/the search icon
  // below still fire immediately for anyone who doesn't want to wait.
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const timer = setTimeout(() => {
      onSearchRef.current(value.trim());
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [value]);

  const handleSearch = (): void => {
    onSearch(value.trim());
  };

  return (
    <TextField
      label={label}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          handleSearch();
        }
      }}
      placeholder={placeholder}
      disabled={disabled}
      fullWidth={fullWidth}
      size="small"
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton onClick={handleSearch} edge="end" disabled={disabled} aria-label="search">
              <SearchIcon />
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
};

export default SearchInput;
